// v1.7.0 · CRM · Panel: estado y valor de oportunidades, conversión, embudo, pronóstico, fuentes de leads y tareas
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { api, errMsg, money, fdate, today, STATUS } from './ui';

const RANGES = [[7, 'Últimos 7 días'], [30, 'Últimos 30 días'], [90, 'Últimos 90 días'], [365, 'Último año'], [0, 'Todo']];

function Card({ title, sub, children, right }) {
  return (
    <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div><h3 className="text-[14px] font-bold text-[#0F2A3A]">{title}</h3>{sub && <p className="text-xs text-gray-500">{sub}</p>}</div>
        {right}
      </div>
      {children}
    </section>
  );
}

export default function CrmDashboard({ meta, onGo }) {
  const [days, setDays] = useState(30);
  const [pipelineId, setPipelineId] = useState('');
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try { setD(await api.get('/dashboard', { days, pipeline_id: pipelineId })); } catch (e) { toast.error(errMsg(e)); }
  }, [days, pipelineId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="py-16 text-center text-sm text-gray-500">Cargando panel…</div>;
  const statusData = Object.entries(d.by_status).map(([k, v]) => ({ key: k, name: STATUS[k].label, value: v.count, money: v.value, color: STATUS[k].color }));
  const valueData = Object.entries(d.by_status).map(([k, v]) => ({ name: STATUS[k].label, value: v.value, color: STATUS[k].color }));
  const users = Object.fromEntries((meta.users || []).map((u) => [u.id, u.name]));

  return (
    <div className="space-y-4" data-testid="crm-dashboard">
      {d.scope === 'own' && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Está viendo solo sus registros (contactos, oportunidades y tareas asignados a usted).</p>}
      <div className="flex flex-wrap items-center gap-2">
        <select className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" value={pipelineId || d.pipeline_id || ''}
                onChange={(e) => setPipelineId(e.target.value)} aria-label="Embudo">
          {meta.pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Periodo">
          {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[['Oportunidades', d.total], ['Valor total', money(d.total_value)], ['Valor ganado', money(d.won_value)],
          ['Tasa de conversión', `${d.conversion_rate} %`], ['Pronóstico ponderado', money(d.forecast)]].map(([l, v]) => (
          <div key={l} className="min-w-0 rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid="crm-kpi">
            <div className="truncate text-[22px] font-extrabold text-[#0F2A3A]">{v}</div>
            <div className="text-xs text-gray-500">{l}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Estado de oportunidades" sub="Cantidad por estado">
          {d.total === 0 ? <p className="py-10 text-center text-sm text-gray-500">Sin oportunidades en este periodo.</p> : (
            <div className="flex items-center gap-4">
              <div className="h-[170px] w-[170px] shrink-0">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={78} paddingAngle={2} stroke="#fff" strokeWidth={2}>
                      {statusData.map((s) => <Cell key={s.key} fill={s.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v}`, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1.5 text-sm">
                {statusData.map((s) => <li key={s.key} className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />{s.name} <b className="ml-auto pl-3">{s.value}</b></li>)}
              </ul>
            </div>
          )}
        </Card>
        <Card title="Valor de oportunidades" sub="Monto por estado">
          {d.total_value === 0 ? <p className="py-16 text-center text-sm text-gray-500">Sin montos en este periodo.</p> : (
          <div className="h-[180px]">
            <ResponsiveContainer>
              <BarChart data={valueData} margin={{ left: 0, right: 8, top: 6 }}>
                <CartesianGrid vertical={false} stroke="#EEF0F3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 100) / 10}K` : v}`} width={44} />
                <Tooltip formatter={(v) => money(v)} cursor={{ fill: '#F3F4F6' }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={44}>{valueData.map((s) => <Cell key={s.name} fill={s.color} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </Card>
        <Card title="Tasa de conversión" sub="Ganadas sobre el total del periodo">
          <div className="flex h-[180px] flex-col items-center justify-center">
            <div className="text-[44px] font-extrabold text-[#2E7A22]">{d.conversion_rate} %</div>
            <div className="text-sm text-gray-500">Cierre efectivo: {d.win_rate} % de las oportunidades cerradas</div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Embudo" sub="Oportunidades abiertas por etapa y su probabilidad de cierre">
          <div className="space-y-2">
            {d.funnel.map((f) => {
              const max = Math.max(1, ...d.funnel.map((x) => x.count));
              return (
                <div key={f.stage_id} className="grid grid-cols-[minmax(0,130px)_minmax(0,1fr)_96px] items-center gap-3 text-sm">
                  <span className="truncate">{f.name} <span className="text-xs text-gray-500">{f.probability} %</span></span>
                  <div className="h-6 rounded bg-gray-100"><div className="h-full rounded bg-[#2a78d6]" style={{ width: `${(f.count / max) * 100}%`, minWidth: f.count ? 6 : 0 }} title={`${f.count} oportunidades`} /></div>
                  <span className="text-right text-xs text-gray-600"><b className="text-gray-900">{f.count}</b> · {money(f.value)}</span>
                </div>
              );
            })}
          </div>
        </Card>
        <Card title="Fuentes de leads" sub="Resultados por origen de la oportunidad">
          {d.sources.length === 0 ? <p className="py-8 text-center text-sm text-gray-500">Sin datos todavía.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500">
                  {['Fuente', 'Total', 'Abiertas', 'Ganadas', 'Perdidas', 'Abandonadas', '% ganadas'].map((h) => <th key={h} className="py-1.5 pr-3 font-semibold">{h}</th>)}
                </tr></thead>
                <tbody>{d.sources.map((s) => (
                  <tr key={s.source} className="border-t">
                    <td className="py-1.5 pr-3 font-medium">{s.source}</td><td className="pr-3">{s.total}</td><td className="pr-3">{s.open}</td>
                    <td className="pr-3">{s.won}</td><td className="pr-3">{s.lost}</td><td className="pr-3">{s.abandoned}</td><td className="pr-3 font-semibold">{s.win_pct} %</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Contactos" sub={days ? `Nuevos en el periodo` : 'Total'}>
          <div className="text-[34px] font-extrabold text-[#0F2A3A]">{d.contacts_new}</div>
          <div className="text-sm text-gray-500">de {d.contacts_total} contactos en total</div>
          <button className="mt-3 text-sm font-semibold text-[#2F3D8E] hover:underline" onClick={() => onGo('contacts')}>Ver contactos</button>
        </Card>
        <Card title="Tareas pendientes" sub={`${d.tasks_pending} pendientes · ${d.tasks_overdue} vencidas`} right={<button className="text-sm font-semibold text-[#2F3D8E] hover:underline" onClick={() => onGo('tasks')}>Ver todas</button>}>
          {d.tasks_next.length === 0 ? <p className="text-sm text-gray-500">No hay tareas pendientes.</p> : (
            <ul className="divide-y text-sm">
              {d.tasks_next.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="truncate">{t.title}</span>
                  <span className={`shrink-0 text-xs ${t.due_date && t.due_date < today() ? 'font-semibold text-red-600' : 'text-gray-500'}`}>
                    {t.due_date ? fdate(t.due_date) : 'Sin fecha'} · {users[t.assigned_to] || 'Sin asignar'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
